import { z } from 'zod';
import { VisualReviewArtifactFields } from './media-provenance.js';
/**
 * auto-artifacts: files an agent "shows the user" (`open`, `xdg-open`, `start`, `qlmanage -p`,
 * `code`), deliverables it produced in a turn, and Claude artifacts (claude.ai Artifact tool) all
 * become encrypted Viko artifacts. These schemas are additive fields on `artifact` events and
 * index entries; everything in them travels only inside sealed content.
 */
/** How an artifact reached Viko. */
export const ArtifactSource = z.enum([
    /** `viko_post_artifact`. */
    'post',
    /** A "show the user" command (`open`, `xdg-open`, `start`, `qlmanage -p`, `code`). */
    'open',
    /** Proactive deliverable detection at turn end. */
    'detect',
    /** The artifact watcher (project capture globs). */
    'watch',
    /** A Claude Code Artifact tool publish (claude.ai artifact). */
    'claude-artifact',
]);
/** Several artifacts shown together: a gallery, an A/B comparison, or one bundled page. */
export const ArtifactCollection = z.object({
    id: z.string().min(1).max(128),
    layout: z.enum(['gallery', 'comparison']),
    title: z.string().max(512).optional(),
    /** Position of this artifact in the collection (0-based) and the collection size. */
    index: z.number().int().nonnegative().max(10_000),
    count: z.number().int().positive().max(10_000),
    /** comparison: the shared key of a pair/row (e.g. `night-1440`). */
    pair: z.string().max(256).optional(),
    /** comparison: this artifact's variant label within its pair (e.g. `A`, `B`, `before`). */
    variant: z.string().max(64).optional(),
});
/**
 * A claude.ai artifact published by a Claude run. Viko's own sealed snapshot is the primary view
 * (no claude.ai login needed); the claude.ai page is secondary and opens as the publishing account.
 */
export const ExternalArtifact = z.object({
    provider: z.literal('claude'),
    /** `https://claude.ai/code/artifact/<uuid>` or `https://claude.ai/artifact/<id>`; absent until known. */
    url: z
        .string()
        .max(2048)
        .regex(/^https:\/\/claude\.ai\/(code\/)?artifact\/[A-Za-z0-9_-]+$/)
        .optional(),
    title: z.string().max(512).optional(),
    description: z.string().max(2000).optional(),
    /** Viko provider account that published it (`claude:work`) and its display label. */
    accountId: z.string().max(256).optional(),
    accountLabel: z.string().max(256).optional(),
    /** Version of this claude.ai artifact (1 = first publish; republishes to the same url add one). */
    version: z.number().int().positive().max(100_000).optional(),
    /** True when this Viko artifact holds a captured snapshot; false/absent = link only. */
    snapshot: z.boolean().optional(),
    /** Why no snapshot exists (source outside the run's scope, missing, too large). */
    snapshotMissing: z.string().max(256).optional(),
    /** A local preview URL the page reads live data from (opens through the live preview). */
    liveUrl: z.string().max(2048).optional(),
});
/** Additive fields for `artifact` events (and the sealed index entry). */
export const AutoArtifactFields = {
    source: ArtifactSource.optional(),
    collection: ArtifactCollection.optional(),
    external: ExternalArtifact.optional(),
    /** A self-contained HTML snapshot (scripts removed, assets inlined, no-network CSP). */
    bundle: z.boolean().optional(),
    /** Detected but not published (project policy "ask"): open it on demand by its path. */
    offered: z.boolean().optional(),
    /**
     * agent-sharing: an offered file in a folder outside the project's readable roots (display form,
     * e.g. `../Artwork`). Publishing it approves that folder as a machine-local asset root.
     */
    shareRoot: z.string().max(1024).optional(),
    // ---- visual-review: commit provenance and sidecar stats of captured media ----
    ...VisualReviewArtifactFields,
    // ---- end visual-review ----
};
/** Per-project policy for proactive deliverable detection (`viko.project.yaml` → `autoArtifacts`). */
export const AutoArtifactPolicy = z.enum(['default', 'auto', 'ask', 'off']);
/** Whether a captured "show" command still opens on this machine. */
export const OpenLocallyPolicy = z.enum(['auto', 'always', 'never']);
/**
 * Publish a file the daemon offered (policy "ask") from the viewer: device-signed, encrypted
 * command to the run's machine; the path is re-scoped there (worktree, deny-list).
 */
export const AUTO_ARTIFACT_COMMAND_PAYLOADS = {
    'run.publish_artifact': z.object({
        runId: z.string().min(1).max(256),
        path: z.string().min(1).max(4096),
    }),
};
export const PublishArtifactResult = z.object({
    artifactIds: z.array(z.string()),
    note: z.string(),
});
//# sourceMappingURL=auto-artifacts.js.map